import React, { Fragment, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import authSlice from '../../store/slices/auth';
import PaymentSelectModal from '../modals/PaymentSelectModal';
import { boostProfile } from '../../utils/paymentUtil';
import { cn } from '@/lib/utils';
import { authHref } from '@/utils/authReturn';
import { useMediaQuery } from '@/hooks/usePerformanceOptimizations';
import { useNightMode } from '@/theme/useNightMode';
import { nightTrackClass } from '@/theme/surfaceClasses';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuGroup,
    DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
    Menu,
    MessageSquare,
    LifeBuoy,
    LogOut,
    LogIn,
    UserPlus,
    Share2,
    Zap,
    Moon,
    ExternalLink,
    type LucideIcon,
} from 'lucide-react';
import NotificationBadge from './NotificationBadge';
import {
    BROWSE_NAV_ITEMS,
    SETTINGS_NAV_ITEM,
    getProfileNavItem,
    getRoleNavItems,
    isNavItemActive,
    type NavItem,
} from './navConfig';
import { useRailVisibleIds } from './railVisible';

// Tailwind `lg`: the side rail and the desktop header.
const DESKTOP_QUERY = '(min-width: 1024px)';

// The header bell opens notifications on a phone, so that row stays out of the mobile menu.
const MOBILE_BROWSE_ITEMS = BROWSE_NAV_ITEMS.filter((item) => item.id !== 'notifications');

// Primary ways into the app. The rail shows them too; the menu keeps them anyway.
const PINNED_MENU_IDS: ReadonlySet<NavItem['id']> = new Set(['discover', 'jamboard']);

const EXTERNAL_LINKS = {
    community: 'https://discord.jamwith.net',
    contact: 'https://www.jamwith.net/contact/',
    about: 'https://www.jamwith.net',
} as const;

// `data-highlighted` is set by Radix for both hover and keyboard focus, so one rule covers both.
// `cursor-pointer` wins over the menu primitive's `cursor-default`, so every row reads as a button.
// A quiet wine, not a bright alert red. The label is the darkest step of that
// wine that still clears WCAG AA on the near-black menu. Hover is a neutral
// surface lift with the menu's normal text, same as the other rows.
const NIGHT_WINE = 'dark:text-jw-wine-text';
const NIGHT_ROW_HOVER = 'dark:data-[highlighted]:bg-jw-surface-2 dark:data-[highlighted]:text-jw-ink';

const ITEM_VARIANTS = {
    default: `cursor-pointer rounded-md data-[highlighted]:bg-gray-100 ${NIGHT_ROW_HOVER}`,
    accent: `cursor-pointer rounded-md data-[highlighted]:bg-purple-50 ${NIGHT_WINE} ${NIGHT_ROW_HOVER}`,
    // Amber is the privilege color: purple is the brand, red is destructive.
    restricted: 'cursor-pointer rounded-md text-amber-950 data-[highlighted]:bg-amber-50 data-[highlighted]:text-amber-950 dark:text-amber-300 dark:data-[highlighted]:bg-amber-950/40',
    danger: `cursor-pointer rounded-md text-red-600 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-600 ${NIGHT_WINE} ${NIGHT_ROW_HOVER}`,
} as const;

const ACTIVE_ITEM_CLASS = 'bg-gray-50 font-semibold text-purple-light dark:bg-jw-wine/[0.72] dark:text-jw-ink dark:data-[highlighted]:bg-jw-wine/90 dark:data-[highlighted]:text-jw-ink';
const RESTRICTED_ACTIVE_CLASS = 'bg-amber-50 font-semibold text-amber-950 dark:bg-amber-950/40 dark:text-amber-300';

type ItemVariant = keyof typeof ITEM_VARIANTS;

// One column for every leading icon. A larger glyph stays centered in it, so its label
// still starts on the same edge as the other rows.
const MenuIcon: React.FC<{
    Icon: LucideIcon
    strokeWidth?: number
    className?: string
}> = ({ Icon, strokeWidth, className = 'h-4 w-4' }) => (
    <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">
        <Icon className={className} strokeWidth={strokeWidth} aria-hidden="true" />
    </span>
);

interface MenuLinkProps {
    to: string
    label: string
    Icon: LucideIcon
    strokeWidth?: number
    isActive?: boolean
    badgeCount?: number
    variant?: Exclude<ItemVariant, 'danger'>
    iconClassName?: string
}

// A real <a>, so middle/Cmd-click, "open in new tab" and screen-reader link lists all work.
const MenuLink: React.FC<MenuLinkProps> = ({
    to,
    label,
    Icon,
    strokeWidth,
    isActive = false,
    badgeCount = 0,
    variant = 'default',
    iconClassName = 'h-4 w-4',
}) => (
    <DropdownMenuItem
        asChild
        className={cn(
            ITEM_VARIANTS[variant],
            isActive && (variant === 'restricted' ? RESTRICTED_ACTIVE_CLASS : ACTIVE_ITEM_CLASS)
        )}
    >
        <Link to={to} aria-current={isActive ? 'page' : undefined} className="group flex w-full items-center gap-2">
            <MenuIcon
                Icon={Icon}
                strokeWidth={strokeWidth}
                className={cn(
                    iconClassName,
                    variant === 'accent' && 'text-purple-500',
                    variant === 'restricted' && 'text-amber-700 dark:group-data-[highlighted]:!text-white'
                )}
            />
            <span>{label}</span>
            <NotificationBadge count={badgeCount} className="ml-auto" />
        </Link>
    </DropdownMenuItem>
);

interface MenuExternalLinkProps {
    href: string
    label: string
    Icon: LucideIcon
}

const MenuExternalLink: React.FC<MenuExternalLinkProps> = ({ href, label, Icon }) => (
    <DropdownMenuItem asChild className={ITEM_VARIANTS.default}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="flex w-full items-center gap-2">
            <MenuIcon Icon={Icon} />
            <span>{label}</span>
            <ExternalLink className="ml-auto h-3 w-3 opacity-50" aria-hidden="true" />
            <span className="sr-only">, opens in a new tab</span>
        </a>
    </DropdownMenuItem>
);

interface MenuActionProps {
    label: string
    Icon: LucideIcon
    onSelect: () => void
    variant?: Exclude<ItemVariant, 'default'>
}

const MenuAction: React.FC<MenuActionProps> = ({ label, Icon, onSelect, variant = 'accent' }) => (
    <DropdownMenuItem onSelect={onSelect} className={cn(ITEM_VARIANTS[variant], 'gap-2')}>
        <MenuIcon Icon={Icon} className={cn('h-4 w-4', variant === 'accent' && 'text-purple-500')} />
        <span>{label}</span>
    </DropdownMenuItem>
);

// Quieter than the action rows: smaller type, and a short rule instead of a "|" glyph,
// which sits low and reads as another character.
const FOOTER_LINK_CLASS =
    'cursor-pointer justify-center whitespace-nowrap rounded-sm px-1.5 py-1 text-xs text-gray-500 transition-colors data-[highlighted]:text-gray-900 data-[highlighted]:underline data-[highlighted]:decoration-gray-300 data-[highlighted]:underline-offset-[3px] dark:text-jw-ink-2 dark:data-[highlighted]:text-jw-ink';

const LegalLink: React.FC<{ to: string; label: string }> = ({ to, label }) => (
    <DropdownMenuItem asChild className={FOOTER_LINK_CLASS}>
        <Link to={to}>{label}</Link>
    </DropdownMenuItem>
);

const FooterDivider: React.FC = () => (
    <span className="h-2.5 w-px shrink-0 self-center bg-gray-300 dark:bg-jw-line" aria-hidden="true" />
);

// Shared by signed-in and signed-out visitors; these destinations are all public.
const NightModeMenuItem: React.FC = () => {
    const { globalOn, setGlobal } = useNightMode();
    return (
        <DropdownMenuCheckboxItem
            checked={globalOn}
            onCheckedChange={(checked) => setGlobal(checked === true)}
            onSelect={(event) => event.preventDefault()}
            className={cn(
                ITEM_VARIANTS.default,
                // Match DropdownMenuItem (text-base, px-2). The primitive is text-sm with room for a checkmark.
                'w-full gap-2 px-2 text-base [&>span:first-child]:hidden'
            )}
        >
            <MenuIcon Icon={Moon} />
            <span>Night mode</span>
            <span
                className={cn(
                    'pointer-events-none relative ml-auto h-5 w-9 shrink-0 rounded-full',
                    nightTrackClass(globalOn)
                )}
                aria-hidden="true"
            >
                <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform duration-150 dark:bg-white', globalOn ? 'translate-x-4' : 'translate-x-0.5')} />
            </span>
        </DropdownMenuCheckboxItem>
    );
};

const HelpGroup: React.FC = () => (
    <DropdownMenuGroup aria-label="Help">
        <MenuExternalLink href={EXTERNAL_LINKS.community} label="Community & Support" Icon={LifeBuoy} />
        <MenuExternalLink href={EXTERNAL_LINKS.contact} label="Contact Us" Icon={MessageSquare} />
    </DropdownMenuGroup>
);

const LegalFooter: React.FC = () => (
    <DropdownMenuGroup aria-label="Legal" className="mt-2 flex items-center justify-center gap-1.5 px-0.5">
        <DropdownMenuItem asChild className={FOOTER_LINK_CLASS}>
            <a href={EXTERNAL_LINKS.about} target="_blank" rel="noopener noreferrer">
                About
                <span className="sr-only">, opens in a new tab</span>
            </a>
        </DropdownMenuItem>
        <FooterDivider />
        <LegalLink to="/privacy-policy" label="Privacy Policy" />
        <FooterDivider />
        <LegalLink to="/terms" label="Terms" />
    </DropdownMenuGroup>
);

interface HeaderDropdownProps {
    referAFriend: () => void;
}

const HeaderDropdown: React.FC<HeaderDropdownProps> = ({
    referAFriend
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { pathname, search, hash } = useLocation();
    const returnTo = `${pathname}${search}${hash}`;
    const user = useSelector((state: RootState) => state.auth.user);
    const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);
    const railVisible = useRailVisibleIds();
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const isDesktop = useMediaQuery(DESKTOP_QUERY);

    const handleLogout = () => {
        dispatch(authSlice.actions.setLogout());
        navigate('/login');
    };

    const handleBoostProfile = () => {
        setShowPaymentModal(true);
    };

    const handlePaymentSelect = async (paymentType: string) => {
        await boostProfile(paymentType);
        setShowPaymentModal(false);
    };

    const renderNavLink = (item: NavItem) => (
        <MenuLink
            key={item.id}
            to={item.to}
            label={item.label}
            Icon={item.Icon}
            strokeWidth={item.strokeWidth}
            iconClassName={item.id === 'discover' || item.id === 'profile' ? 'h-[18px] w-[18px]' : undefined}
            isActive={isNavItemActive(pathname, item)}
            badgeCount={item.id === 'notifications' ? unreadCount : 0}
            variant={item.restricted ? 'restricted' : 'default'}
        />
    );

    const roleItems = getRoleNavItems(user);

    // On desktop the menu repeats a rail destination only when the rail is not showing it. Rendering
    // (not CSS-hiding) keeps those rows out of Radix's arrow-key order. Discover, JamBoard, Profile,
    // Boost and Refer stay at every width. Until the rail reports in, nothing is treated as covered.
    const coveredByRail = (item: NavItem) => isDesktop && railVisible.has(item.id) && !PINNED_MENU_IDS.has(item.id);
    const browseItems = (isDesktop ? BROWSE_NAV_ITEMS : MOBILE_BROWSE_ITEMS).filter((item) => !coveredByRail(item));
    const dashboardItems = roleItems.filter((item) => !coveredByRail(item));

    const memberSections: { key: string; node: React.ReactNode }[] = [];
    if (user) {
        if (browseItems.length > 0) {
            memberSections.push({
                key: 'browse',
                node: <DropdownMenuGroup aria-label="Browse">{browseItems.map(renderNavLink)}</DropdownMenuGroup>,
            });
        }
        memberSections.push({
            key: 'account',
            node: (
                <DropdownMenuGroup aria-label="Account">
                    {[getProfileNavItem(user), SETTINGS_NAV_ITEM].map(renderNavLink)}
                </DropdownMenuGroup>
            ),
        });
        if (dashboardItems.length > 0) {
            memberSections.push({
                key: 'dashboard',
                node: (
                    <DropdownMenuGroup aria-label="Dashboards">
                        {dashboardItems.map(renderNavLink)}
                    </DropdownMenuGroup>
                ),
            });
        }
        memberSections.push({
            key: 'grow',
            node: (
                <DropdownMenuGroup aria-label="Grow your profile">
                    <MenuAction label="Boost Your Profile" Icon={Zap} onSelect={handleBoostProfile} />
                    <MenuAction label="Refer a friend" Icon={Share2} onSelect={referAFriend} />
                </DropdownMenuGroup>
            ),
        });
        memberSections.push({ key: 'help', node: <HelpGroup /> });
    }

    return (
        <>
            <PaymentSelectModal
                show={showPaymentModal}
                setShow={setShowPaymentModal}
                onPaymentSelect={handlePaymentSelect}
                onClose={() => setShowPaymentModal(false)}
            />
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="static"
                        size="icon"
                        tabIndex={0}
                        aria-label="Menu"
                        className="header-menu-button bg-lightblue-100 w-14 h-11 border-2 border-lightblue-200 focus:!outline-none dark:bg-jw-chrome dark:border-jw-line-strong"
                        onMouseDown={(e) => {
                            // Prevent focus outline when clicking with mouse
                            e.currentTarget.classList.remove('focus-visible');
                            document.body.classList.remove('user-is-tabbing');
                        }}
                    >
                        <Menu
                            className="h-7 w-8 text-black dark:!text-white"
                            strokeWidth={1.5}
                        />
                    </Button>
                </DropdownMenuTrigger>
                {/* Capped to the space Radix measures so a long menu scrolls instead of clipping.
                    `!` is needed: a global `.overflow-hidden { ... !important }` would otherwise win. */}
                <DropdownMenuContent
                    className="header-menu max-h-[var(--radix-dropdown-menu-content-available-height)] w-64 !overflow-y-auto bg-white p-2 font-sans dark:bg-jw-chrome dark:text-jw-ink"
                    chrome
                    align="end"
                    sideOffset={8}
                >
                    {user ? (
                        <>
                            {memberSections.map((section, index) => (
                                <Fragment key={section.key}>
                                    {index > 0 && <DropdownMenuSeparator className="my-2" />}
                                    {section.node}
                                </Fragment>
                            ))}

                            <DropdownMenuSeparator className="my-2" />

                            <NightModeMenuItem />

                            <DropdownMenuSeparator className="my-2" />

                            <MenuAction label="Logout" Icon={LogOut} onSelect={handleLogout} variant="danger" />
                        </>
                    ) : (
                        <>
                            <DropdownMenuGroup aria-label="Join JamWith">
                                <MenuLink to={authHref('signup', returnTo)} label="Join Free" Icon={UserPlus} variant="accent" />
                                <MenuLink to={authHref('login', returnTo)} label="Log In" Icon={LogIn} />
                            </DropdownMenuGroup>

                            <DropdownMenuSeparator className="my-2" />

                            <HelpGroup />

                            <DropdownMenuSeparator className="my-2" />

                            <NightModeMenuItem />
                        </>
                    )}
                    <LegalFooter />
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
};

export default HeaderDropdown;
